import type { CloseMarketDayResult, MarketDailyClosing } from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  DateInput,
  DateRangePicker,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CalendarCheck, Lock } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Th } from "../../../features/fuel/shared.js";
import { moneyExport } from "../../../features/market/market-values.js";
import {
  type ClosingFilters,
  closingsQuery,
  useDailyClosings,
  useMarketPermissions,
  useRefreshMarket,
} from "../../../features/market/queries.js";
import {
  LedgerStatus,
  MarketPage,
  marketSearchShape,
  MarketStationField,
  type MarketStationOption,
  NoStations,
  TableFrame,
  useMarketStations,
} from "../../../features/market/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatCount, formatDate, formatDateTime } from "../../../lib/format.js";
import { useStepUp } from "../../../lib/step-up.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({ ...marketSearchShape, from: localDateText, to: localDateText });

export const Route = createFileRoute("/_app/market/gun-kapanislari")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ClosingsRoute,
});

/**
 * Market gün kapanışları (Prompt 16 — web): istasyon × iş günü başına TEK SMM fişi (`COGS` / `INVENTORY`,
 * muhasebe grubu × iş birimi satırlarıyla). Normalde vardiya GÜN SONU akışı kendiliğinden tetikler; elle kapanış
 * `shifts.day.close` + step-up ister ve idempotenttir (o gün kapanmışsa mevcut kapanış döner). Kapanıştan sonra
 * işlenen geç hareket o güne yazılmaz, sonraki kapanışa "önceki günlerden" satırıyla girer.
 */
function ClosingsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketDailyClosings")}
      description={t("market.closings.description")}
      breadcrumb={[{ label: t("menu.marketDailyClosings") }]}
    >
      {({ companyId }) => <ClosingsBody companyId={companyId} />}
    </MarketPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -13), to: today };
}

function ClosingsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const station = useMarketStations(companyId);
  const [closing, setClosing] = useState(false);
  const [selected, setSelected] = useState<MarketDailyClosing | null>(null);
  const fallback = defaultRange();
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: ClosingFilters = {
    stationId: station.stationId ?? undefined,
    from: range.from,
    to: range.to,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useDailyClosings(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<MarketDailyClosing>[]>(
    () => [
      {
        id: "businessDate",
        header: t("market.closings.fields.businessDate"),
        hideable: false,
        exportValue: (row) => row.businessDate,
        cell: (row) => <span className="tabular font-medium">{formatDate(row.businessDate)}</span>,
      },
      {
        id: "station",
        header: t("market.fields.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName,
      },
      {
        id: "cogs",
        header: t("market.closings.fields.cogs"),
        align: "right",
        exportValue: (row) => moneyExport(row.cogsAmount),
        cell: (row) => <MoneyText value={row.cogsAmount} colorize className="font-medium" />,
      },
      {
        id: "movementCount",
        header: t("market.closings.fields.movementCount"),
        align: "right",
        exportValue: (row) => row.movementCount,
        cell: (row) => <span className="tabular">{formatCount(row.movementCount)}</span>,
      },
      {
        id: "lateMovementCount",
        header: t("market.closings.fields.lateMovementCount"),
        align: "right",
        exportValue: (row) => row.lateMovementCount,
        cell: (row) => <span className="tabular">{formatCount(row.lateMovementCount)}</span>,
      },
      {
        id: "ledger",
        header: t("market.fields.ledger"),
        exportValue: (row) => row.ledgerStatus,
        cell: (row) => <LedgerStatus status={row.ledgerStatus} journalEntryId={row.journalEntryId} />,
      },
      {
        id: "closedAt",
        header: t("market.closings.fields.closedAt"),
        exportValue: (row) => row.closedAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.closedAt)}</span>,
      },
      {
        id: "detail",
        header: t("common.actions"),
        cell: (row) => (
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("market.closings.openLines", {
              station: row.stationName,
              date: formatDate(row.businessDate),
            })}
            onClick={() => setSelected(row)}
          >
            {t("market.closings.lines")}
          </Button>
        ),
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        <DateRangePicker
          label={t("market.closings.range")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
        {permissions.canCloseDay ? (
          <Button variant="outline" className="ms-auto" onClick={() => setClosing(true)}>
            <Lock aria-hidden="true" />
            {t("market.closings.closeDay")}
          </Button>
        ) : null}
      </Card>

      <DataTable
        caption={t("market.closings.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<CalendarCheck />}
            title={t("market.closings.emptyTitle")}
            description={t("market.closings.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<MarketDailyClosing>(
            async (cursor) =>
              (
                await api.GET("/market/daily-closings", {
                  params: { query: closingsQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "market-gun-kapanislari",
            sheetName: t("menu.marketDailyClosings"),
            columns: visible,
            rows,
          });
        }}
      />

      <Drawer
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        title={t("market.closings.linesTitle")}
        {...(selected
          ? { description: `${selected.stationName} · ${formatDate(selected.businessDate)}` }
          : {})}
      >
        {selected ? <ClosingLines closing={selected} /> : null}
      </Drawer>

      {permissions.canCloseDay ? (
        <CloseDayDialog
          open={closing}
          onOpenChange={setClosing}
          stations={station.stations}
          defaultStationId={station.stationId}
        />
      ) : null}
    </>
  );
}

function ClosingLines({ closing }: { closing: MarketDailyClosing }): ReactElement {
  const { t } = useTranslation();
  if (closing.lines.length === 0)
    return <p className="text-sm text-muted-foreground">{t("market.closings.noLines")}</p>;
  return (
    <TableFrame label={t("market.closings.linesTitle")}>
      <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
        <tr>
          <Th>{t("market.closings.fields.accountingGroup")}</Th>
          <Th align="right">{t("market.closings.fields.saleValue")}</Th>
          <Th align="right">{t("market.closings.fields.returnValue")}</Th>
          <Th align="right">{t("market.closings.fields.lateValue")}</Th>
          <Th align="right">{t("market.closings.fields.costAdjustment")}</Th>
          <Th align="right">{t("market.closings.fields.cogs")}</Th>
        </tr>
      </thead>
      <tbody>
        {closing.lines.map((line) => (
          <tr key={`${line.accountingGroup}:${line.businessUnit}`} className="border-t">
            <td className="px-3 py-2">
              <span className="grid gap-0.5">
                <span className="font-mono text-xs">{line.accountingGroup}</span>
                <span className="text-xs text-muted-foreground">
                  {t(`enums.businessUnit.${line.businessUnit}`)}
                </span>
              </span>
            </td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={line.saleValue} />
            </td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={line.returnValue} />
            </td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={line.lateValue} colorize />
            </td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={line.costAdjustment} colorize />
            </td>
            <td className="px-3 py-2 text-right font-medium">
              <MoneyText value={line.cogs} colorize />
            </td>
          </tr>
        ))}
      </tbody>
    </TableFrame>
  );
}

function CloseDayDialog({
  open,
  onOpenChange,
  stations,
  defaultStationId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stations: readonly MarketStationOption[];
  defaultStationId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const withStepUp = useStepUp();
  const [stationId, setStationId] = useState(defaultStationId ?? stations[0]?.id ?? "");
  const [businessDate, setBusinessDate] = useState<LocalDate | null>(addDays(toIstanbulDate(new Date()), -1));
  const [result, setResult] = useState<CloseMarketDayResult | null>(null);

  const close = useMutation({
    mutationFn: async () =>
      withStepUp(
        async () =>
          (
            await api.POST("/market/daily-closings", {
              body: { stationId, businessDate: businessDate! },
            })
          ).data!,
      ),
    onSuccess: async (data) => {
      setResult(data as CloseMarketDayResult);
      await refresh();
      toast({
        tone: "success",
        title: data.created ? t("market.closings.closed") : t("market.closings.alreadyClosed"),
      });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setResult(null);
      }}
      title={t("market.closings.closeTitle")}
      description={t("market.closings.closeDescription")}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (stationId && businessDate) close.mutate();
        }}
      >
        {stations.length > 1 ? (
          <FormField label={t("market.fields.station")} required>
            {(control) => (
              <Select {...control} value={stationId} onChange={(event) => setStationId(event.target.value)}>
                {stations.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.code} {station.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
        <FormField label={t("market.closings.fields.businessDate")} required>
          {(control) => (
            <DateInput
              {...control}
              value={businessDate}
              max={toIstanbulDate(new Date())}
              onValueChange={setBusinessDate}
            />
          )}
        </FormField>
        {result ? (
          <Alert tone={result.warnings.length > 0 ? "warning" : "success"}>
            <div className="grid gap-1">
              <p>
                {t("market.closings.resultCogs", {
                  amount: moneyExport(result.closing.cogsAmount) ?? "0,00",
                  count: result.closing.movementCount,
                })}
              </p>
              {result.warnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
          </Alert>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
          <Button type="submit" loading={close.isPending} disabled={!stationId || !businessDate}>
            <Lock aria-hidden="true" />
            {t("market.closings.closeDay")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
